import { Button, FormControl, Grid2, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { useState } from "react";

import FilterStyles from "./style";

import { useGenreList } from "@/api/hooks/useGenre";
import { useTags } from "@/api/hooks/useTags";
import { usePublisherList } from "@/api/hooks/publisher";
import { IFilter } from "@/api/types/content";

export interface IFilterValues {
    search: string;
    filter: IFilter;
}

export interface IApplyDataType {
    search: string;
    filter: Omit<IFilter, "category">;
}

const defaultValues: IFilterValues = {
    search: "",
    filter: {
        tag: null,
        type: null,
        genre: null,
        status: null,
        category: null,
        publisher: null,
    },
};

// eslint-disable-next-line no-unused-vars
export default function Filters({ onApply }: { onApply: (data: IApplyDataType) => void }) {
    const [fields, setFields] = useState(defaultValues);
    const { data: genresList } = useGenreList({
        skip: 0,
        take: 1000,
    });

    const { data: dataTags } = useTags({
        skip: 0,
        take: 1000,
    });

    const { data: publishersData } = usePublisherList({
        skip: 0,
        take: 1000,
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        const name = event.target.name;
        const value = event.target.value;

        setFields((prev) => {
            return {
                ...prev,
                [name]: value,
            };
        });
    };

    const handleChangeFilter = (event: React.ChangeEvent<HTMLTextAreaElement> | SelectChangeEvent) => {
        const name = event.target.name;
        const value = event.target.value;

        setFields((prev) => {
            return {
                ...prev,
                filter: {
                    ...prev.filter,
                    [name]: value,
                },
            };
        });
    };

    const handleReset = () => {
        setFields(defaultValues);

        if (onApply) onApply({ ...defaultValues, filter: { ...defaultValues.filter } });
    };

    const handleApply = () => {
        const filter = {
            ...fields.filter,
        };

        onApply && onApply({ search: fields.search, filter });
    };

    const Genres =
        genresList &&
        genresList.items
            .filter((item) => {
                if (fields.filter.category === "all" || fields.filter.category === null) return item;

                return Boolean(fields.filter.category === "education") ? item.isEducation : item.isEntertainment;
            })
            .map((genre) => ({ value: genre.id, label: genre.ml_title.en }));

    const TagList =
        dataTags &&
        dataTags.data.items
            .filter((item) => {
                if (fields.filter.category === "all" || fields.filter.category === null) return item;

                return Boolean(fields.filter.category === "education") ? item.isEducation : item.isEntertainment;
            })
            .map((tag) => ({ value: tag.id, label: tag.ml_title.en }));

    const Publishers = publishersData?.data.items;

    return (
        <FilterStyles>
            <Grid2 container className="filters-grid" spacing={2}>
                <Grid2 className="filters-grid-item" size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                    <TextField
                        fullWidth
                        className="input field "
                        label="Search"
                        name="search"
                        placeholder="Enter some text"
                        size="small"
                        value={fields.search}
                        onChange={handleChange}
                    />
                </Grid2>
                <Grid2 className="filters-grid-item" size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                    <FormControl fullWidth className="select field" size="small">
                        <InputLabel id="select-label">Type</InputLabel>
                        <Select
                            id="select"
                            label="Type"
                            labelId="select-label"
                            name="type"
                            value={String(fields.filter.type)}
                            onChange={handleChangeFilter}
                        >
                            <MenuItem value={null as any}>All</MenuItem>
                            <MenuItem value={1}>Movie</MenuItem>
                            <MenuItem value={2}>Series</MenuItem>
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 className="filters-grid-item" size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                    <FormControl fullWidth className="select field" size="small">
                        <InputLabel id="select-label">Category</InputLabel>
                        <Select
                            id="select"
                            label="Category"
                            labelId="select-label"
                            name="category"
                            value={String(fields.filter.category)}
                            onChange={handleChangeFilter}
                        >
                            <MenuItem value={null as any}>Not Selected</MenuItem>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="education">Education</MenuItem>
                            <MenuItem value="entertainment">Entertainment</MenuItem>
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 className="filters-grid-item" size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                    <FormControl fullWidth className="select field" size="small">
                        <InputLabel id="select-label">State</InputLabel>
                        <Select
                            id="select"
                            label="State"
                            labelId="select-label"
                            name="status"
                            value={String(fields.filter.status)}
                            onChange={handleChangeFilter}
                        >
                            <MenuItem value={null as any}>All</MenuItem>
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="published">Published</MenuItem>
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 className="filters-grid-item" size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                    <FormControl fullWidth className="select field" size="small">
                        <InputLabel id="select-label">Tag</InputLabel>
                        <Select
                            id="select"
                            label="Tag"
                            labelId="select-label"
                            name="tag"
                            value={String(fields.filter.tag)}
                            onChange={handleChangeFilter}
                        >
                            <MenuItem value={null as any}>All</MenuItem>
                            {TagList?.map((item) => {
                                return (
                                    <MenuItem key={item.label} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 className="filters-grid-item" size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                    <FormControl fullWidth className="select field" size="small">
                        <InputLabel id="select-label">Genre</InputLabel>
                        <Select
                            id="select"
                            label="Genre"
                            labelId="select-label"
                            name="genre"
                            value={String(fields.filter.genre)}
                            onChange={handleChangeFilter}
                        >
                            <MenuItem value={null as any}>All</MenuItem>
                            {Genres?.map((item) => {
                                return (
                                    <MenuItem key={item.label} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 className="filters-grid-item" size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                    <FormControl fullWidth className="select field" size="small">
                        <InputLabel id="select-label">Publisher</InputLabel>
                        <Select
                            id="select"
                            label="Publisher"
                            labelId="select-label"
                            name="publisher"
                            value={String(fields.filter.publisher)}
                            onChange={handleChangeFilter}
                        >
                            <MenuItem value={null as any}>All</MenuItem>
                            {Publishers?.map((item) => {
                                return (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.title}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 className="filters-grid-item" size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 6 }}>
                            <Button fullWidth className="action-btn" variant="contained" onClick={handleApply}>
                                Apply
                            </Button>
                        </Grid2>
                        <Grid2 size={{ xs: 6 }}>
                            <Button fullWidth className="action-btn" variant="contained" onClick={handleReset}>
                                reset
                            </Button>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Grid2>
        </FilterStyles>
    );
}
